import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  FolderOpen,
  ArrowRight,
  Sparkles,
  AtSign,
  Link2,
} from 'lucide-react';

interface EmptyStateProps {
  onOpenProject?: () => void;
  onStartOnboarding?: () => void;
}

export function EmptyState({
  onOpenProject,
  onStartOnboarding,
}: EmptyStateProps) {
  // ALL HOOKS MUST COME BEFORE THE EARLY RETURN TO AVOID HOOKS VIOLATIONS
  const { currentProject } = useSelector((state: RootState) => state.project);

  if (currentProject) {
    return null; // Don't show empty state if project is selected
  }

  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-2xl text-center space-y-6">
        {/* Icon */}
        <div className="mx-auto w-24 h-24 bg-primary/10 rounded-3xl flex items-center justify-center">
          <FileText className="h-12 w-12 text-primary" />
        </div>

        {/* Main Message */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Welcome to Phrasewright</h1>
          <p className="text-xl text-muted-foreground">
            Start by opening a project to begin managing your reusable content
          </p>
        </div>

        {/* Quick Features Overview */}
        <div className="grid md:grid-cols-2 gap-4 text-left">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AtSign className="h-4 w-4 text-primary" />@ Reference System
              </CardTitle>
              <CardDescription className="text-sm">
                Link content with{' '}
                <code className="px-1 py-0.5 bg-muted rounded text-xs">
                  @documents/file.md
                </code>
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Live Previews
              </CardTitle>
              <CardDescription className="text-sm">
                See referenced content inline while you edit
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-primary" />
                Folder Integration
              </CardTitle>
              <CardDescription className="text-sm">
                Work directly with your existing markdown files
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Link2 className="h-4 w-4 text-primary" />
                Smart Assembly
              </CardTitle>
              <CardDescription className="text-sm">
                Build complex documents from reusable components
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button onClick={onStartOnboarding} size="lg" className="gap-2">
            <Sparkles className="h-4 w-4" />
            Get Started
            <ArrowRight className="h-4 w-4" />
          </Button>

          <Button
            onClick={onOpenProject}
            variant="outline"
            size="lg"
            className="gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            Open Existing Project
          </Button>
        </div>

        <div className="text-sm text-muted-foreground">
          or use the{' '}
          <Badge variant="outline" className="mx-1">
            +
          </Badge>{' '}
          button in the sidebar
        </div>

        {/* Help Text */}
        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            New to Phrasewright? The onboarding flow will guide you through
            creating your first project with sample content.
          </p>
        </div>
      </div>
    </div>
  );
}
